<template>
  <div class="phone-country-container" ref="countryContainer">
    <phone-country-selector :label="label" v-model="country" :disabled="disabled"/>
    <div>
      <input
        class="form-control form-control-sm"
        :type="'tel'"
        :label="label"
        v-model="phoneNumber"
        @keydown="(e) => { lastKeyPressed = e.keyCode }"
        :class="{'is-invalid' : emptyNumber || formError || phoneNumber && !isValid}"
        :disabled="disabled"
        @focus="$emit('focus', $event)"
        @blur="$emit('blur', $event)"
      />
      <span class="invalid-feedback d-block" v-if="phoneNumber && !isValid">Número inválido!</span>
      <span class="invalid-feedback d-block" v-if="(emptyNumber || formError) && required">Campo obrigatório!</span>
    </div>
  </div>
</template>
<script>
import { parsePhoneNumberFromString, AsYouType } from 'libphonenumber-js';
import PhoneCountrySelector from "@/components/shared/phone/countrySelector";
import {mapGetters} from "vuex";

export default {
  name: 'form-phone-country',
  components: {PhoneCountrySelector},
  props: {
    value: { type: Object, default() {return {}}},
    disabled: {type: Boolean, default: false},
    label: { type: String, default: 'Telefone' },
    formError: {type: Boolean, default: false},
    required: {type: Boolean, default: false},
    countryCode: {type: String, default: 'BR'},
  },
  data() {
    return {
      country: {},
      lastKeyPressed: null,
      isValid: true,
    }
  },
  computed: {
    ...mapGetters('locations', ['getCountryById']),
    phoneNumber: {
      get() {
        if(!this.value || !this.value.phone){
          return '';
        }
        return this.value.phone.replace(/^\+\d{1,4}/, '');
      },
      set(newPhone) {
        this.emitValues({ 
          countryCode: this.country.iso2, 
          phoneNumber: newPhone, 
          dialCode: this.country.dialCode 
        });
      }
    },
    emptyNumber(){
      return  (!this.phoneNumber || this.phoneNumber==='') && this.required && this.value;
    },
    defaultCountryCode(){
      return this.countryCode;
    }
  },
  methods: {
    getAsYouTypeFormat(payload) {
      const { countryCode, phoneNumber } = payload;
      const asYouType = new AsYouType(countryCode);
      return phoneNumber ? asYouType.input(phoneNumber) : null;
    },
    getParsePhoneNumberFromString({ phoneNumber, countryCode }) {
      const parsing = phoneNumber && countryCode ? parsePhoneNumberFromString(phoneNumber, countryCode) : null;
      const data = {
        countryCode: countryCode,
        isValid: false,
        ...(phoneNumber && (phoneNumber !== '')
            ? { phoneNumber: phoneNumber }
            : null
        ),
        ...(parsing
            ? {
              countryCallingCode: parsing.countryCallingCode,
              formattedNumber: parsing.number,
              nationalNumber: parsing.nationalNumber,
              isValid: parsing.isValid(),
              type: parsing.getType(),
              formatInternational: parsing.formatInternational(),
              formatNational: parsing.formatNational(),
              uri: parsing.getURI(),
              e164: parsing.format('E.164')
            }
            : null
        )
      };
      
      this.isValid = data.isValid;
      return data;
    },
    setLocale(locale) {
      this.country.countryCode = locale;
      this.emitValues({
        countryCode: locale, 
        phoneNumber: this.phoneNumber, 
        dialCode: this.country.dialCode
      });
    },
    emitValues(payload) {
      let asYouType = this.getAsYouTypeFormat(payload);
      const backSpacePressed = this.lastKeyPressed === 8;
      
      this.$nextTick(() => {
        const lastCharacOfPhoneNumber = this.phoneNumber ? this.phoneNumber.trim().slice(-1) : false;
        if (backSpacePressed && lastCharacOfPhoneNumber && (lastCharacOfPhoneNumber.slice(-1) === ')')) {
          asYouType = this.phoneNumber.slice(0, -2);
          payload.phoneNumber = this.phoneNumber.slice(0, -2);
        }
        
        this.results = this.getParsePhoneNumberFromString(payload);
        this.$emit('update', this.results);
        this.$emit('input', { 
          phone: asYouType, 
          countryCode: this.country.iso2, 
          dialCode: this.country.dialCode,
          formatted: asYouType,
          national: this.results?.nationalNumber || payload.phoneNumber,
          international: this.results?.formatInternational || `+${this.country.dialCode} ${payload.phoneNumber}`,
          isValid: this.results?.isValid || false
        });
      });
    },
    updateCountry(evt){
      this.country = evt;
    }
  },
  watch: {
    phoneNumber: {
      handler(newValue, oldValue) {
        if (newValue && (newValue !== oldValue)) {
          newValue = newValue.replace(/^\+\d{1,4}/, '');
          const phoneNumber = parsePhoneNumberFromString(newValue, this.country.iso2);
          if (phoneNumber) {
            this.emitValues({
              phoneNumber: phoneNumber.nationalNumber,
              countryCode: this.country.iso2 ? this.country.iso2 : phoneNumber.country,
              dialCode: this.country.dialCode
            });
          }
        }
      },
      immediate: false
    },
    country: {
      handler(newValue, oldValue) {
        if (newValue.iso2 === oldValue.iso2) return;
        this.setLocale(newValue.iso2);
      },
      deep: true
    },
    'country.iso2'(newCountryCode, oldCountryCode) {
      if (newCountryCode !== oldCountryCode && this.phoneNumber) {
        this.$nextTick(() => {
          this.emitValues({
            countryCode: newCountryCode,
            phoneNumber: this.phoneNumber,
            dialCode: this.country.dialCode
          });
        });
      }
    },
    value: {
      handler(newValue, oldValue) {  
        if (newValue && newValue.countryCode && newValue.countryCode !== oldValue?.countryCode) {
          this.country = {
            iso2: newValue.countryCode,
          };
        }
        
        if (newValue && newValue.phone && !this.country?.iso2) {
          this.country = {
            iso2: newValue.countryCode || this.defaultCountryCode,
          };
        }
      },
      deep: true,
      immediate: false
    },
  },
  mounted() {
    if (this.value && this.value.phone && this.value.countryCode) {
      this.country.iso2 = this.value.countryCode;
      this.emitValues({
        countryCode: this.value.countryCode, 
        phoneNumber: this.value.phone.replace(/^\+\d{1,4}/, '')
      });
      return;
    }

    if (!this.value || !this.value.phone) {
      this.country.iso2 = this.value?.countryCode || this.defaultCountryCode;
      return;
    }

    if (this.value.countryCode && this.value.phone) {
      this.country.iso2 = this.value.countryCode;
    } else if (!this.value.countryCode && this.value.phone) {
      this.country.iso2 = this.defaultCountryCode;
      this.emitValues({
        countryCode: this.defaultCountryCode, 
        phoneNumber: this.value.phone
      });
    } else if (this.defaultCountryCode) {
      this.country.iso2 = this.defaultCountryCode;
      this.emitValues({
        countryCode: this.defaultCountryCode, 
        phoneNumber: this.value.phone || ''
      });
    }
  },
}
</script>

<style scoped lang="scss">
.phone-country-container {
  display: grid;
  grid-row-gap: .6rem;
  grid-column-gap: .6rem;
  grid-template-columns: 1fr 2fr;
}

</style>